import { DragEvent, useRef, useState } from 'react';
import {
    ChatContainer,
    ConversationHeader,
    InputToolbox,
    MainContainer,
    Message,
    MessageList,
} from '@chatscope/chat-ui-kit-react';
import { format } from 'date-fns';

import style from './SupportWidget.module.scss';

import { SupportWidgetTestAttributes } from 'chaika-test-attributes';

import { translate } from '@chaika/i18n/helper';
import ArrowLeftIcon from '@components/Icon/svg/arrow_left.svg';
import CrossIcon from '@components/Icon/svg/cross.svg';

import { OutgoingSupportMessage, useTicketDetail } from './hooks/useTicketDetail';
import { useSupportComposer } from './hooks/useSupportComposer';
import { useOpeningScrollPosition } from './hooks/useOpeningScrollPosition';
import { LocalSupportAttachmentPreview } from './SupportAttachmentPreview';
import { SupportComposer } from './SupportComposer';
import { TicketHeaderContent } from './TicketHeaderContent';
import { TicketMessage } from './TicketMessage';

const SCROLL_END_TOLERANCE_PX = 1;

interface TicketDetailProps {
    ticketId: string;
    unreadCount: number;
    isPointerOver: boolean;
    onBack(): void;
    onClose(): void;
}

export function TicketDetail({ ticketId, unreadCount, isPointerOver, onBack, onClose }: TicketDetailProps) {
    const ticketDetailRef = useRef<HTMLDivElement>(null);
    const [isComposerFocused, setIsComposerFocused] = useState(false);
    const [isScrolledToLatestMessage, setIsScrolledToLatestMessage] = useState(true);
    const {
        ticket,
        messages,
        outgoingMessages,
        isLoading,
        error,
        hasOlderMessages,
        loadOlderMessages,
        isLoadingOlderMessages,
        sendMessage,
        retryMessage,
        openAttachment,
        isSendingMessage,
        refreshMessages,
        isReadOnly,
    } = useTicketDetail({
        ticketId,
        unreadCount,
        isWatchingLatestMessages: (isComposerFocused || isPointerOver) && isScrolledToLatestMessage,
        onTicketNotFound: onBack,
    });
    const composer = useSupportComposer({
        isDisabled: isLoading || Boolean(error) || isReadOnly,
        isSending: isSendingMessage,
        onSend: sendMessage,
    });
    const openingScrollPosition = useOpeningScrollPosition({ ticketDetailRef, messages, unreadCount });

    const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
        if (!composer.isDisabled) composer.attachFiles(Array.from(event.dataTransfer.files));
    };

    return (
        <div
            ref={ticketDetailRef}
            className={style.ticketDetail}
            data-test={SupportWidgetTestAttributes.TicketDetail}
            data-testid={SupportWidgetTestAttributes.TicketDetail}
            onPaste={composer.handlePaste}
            onDragOver={event => event.preventDefault()}
            onDrop={handleDrop}
        >
            <MainContainer>
                <ChatContainer>
                    <ConversationHeader>
                        <ConversationHeader.Back>
                            <button
                                className={style.headerButton}
                                type={'button'}
                                data-test={SupportWidgetTestAttributes.BackButton}
                                data-testid={SupportWidgetTestAttributes.BackButton}
                                aria-label={translate('supportBack')}
                                title={translate('supportBack')}
                                onClick={onBack}
                            >
                                <ArrowLeftIcon />
                            </button>
                        </ConversationHeader.Back>
                        <ConversationHeader.Content>
                            <TicketHeaderContent ticket={ticket} />
                        </ConversationHeader.Content>
                        <ConversationHeader.Actions>
                            <button
                                className={style.headerButton}
                                type={'button'}
                                data-test={SupportWidgetTestAttributes.CloseButton}
                                data-testid={SupportWidgetTestAttributes.CloseButton}
                                aria-label={translate('supportClose')}
                                title={translate('supportClose')}
                                onClick={onClose}
                            >
                                <CrossIcon />
                            </button>
                        </ConversationHeader.Actions>
                    </ConversationHeader>
                    <MessageList
                        loadingMore={isLoadingOlderMessages}
                        loadingMorePosition={'top'}
                        disableOnYReachWhenNoScroll
                        onYReachStart={() => {
                            if (hasOlderMessages && !isLoadingOlderMessages) {
                                loadOlderMessages();
                            }
                        }}
                        onScrollCapture={event =>
                            setIsScrolledToLatestMessage(isScrolledToEnd(event.target as HTMLElement))
                        }
                        onLoadCapture={openingScrollPosition.keepOpeningPosition}
                        onWheel={openingScrollPosition.releaseOpeningPosition}
                        onTouchStart={openingScrollPosition.releaseOpeningPosition}
                        onKeyDown={openingScrollPosition.releaseOpeningPosition}
                        onMouseDown={openingScrollPosition.releaseOpeningPosition}
                    >
                        {hasOlderMessages && (
                            <div className={style.olderMessagesAction}>
                                <button
                                    className={style.loadMoreButton}
                                    type={'button'}
                                    data-test={SupportWidgetTestAttributes.LoadOlderMessagesButton}
                                    data-testid={SupportWidgetTestAttributes.LoadOlderMessagesButton}
                                    disabled={isLoadingOlderMessages}
                                    onClick={() => loadOlderMessages()}
                                >
                                    {translate('supportLoadOlderMessages')}
                                </button>
                            </div>
                        )}
                        {isLoading && (
                            <MessageList.Content className={style.state}>
                                {translate('supportLoading')}
                            </MessageList.Content>
                        )}
                        {error && (
                            <MessageList.Content className={style.error}>
                                {translate('supportError')}
                            </MessageList.Content>
                        )}
                        {!isLoading && !error && messages.length === 0 && outgoingMessages.length === 0 && (
                            <MessageList.Content className={style.state}>
                                {translate('supportEmptyMessages')}
                            </MessageList.Content>
                        )}
                        {messages.map(message => (
                            <TicketMessage
                                key={message.id}
                                message={message}
                                participants={ticket?.participants ?? []}
                                onOpenAttachment={openAttachment}
                                onUrlExpired={refreshMessages}
                            />
                        ))}
                        {outgoingMessages.map(message => (
                            <OptimisticMessage
                                key={message.clientId}
                                message={message}
                                isSendingMessage={isSendingMessage}
                                onRetry={retryMessage}
                            />
                        ))}
                    </MessageList>
                    <InputToolbox>
                        {isReadOnly && (
                            <div className={style.deletedExplanation}>{translate('supportDeletedExplanation')}</div>
                        )}
                        <SupportComposer
                            composer={composer}
                            onFocus={() => setIsComposerFocused(true)}
                            onBlur={() => setIsComposerFocused(false)}
                        />
                    </InputToolbox>
                </ChatContainer>
            </MainContainer>
        </div>
    );
}

interface OptimisticMessageProps {
    isSendingMessage: boolean;
    message: OutgoingSupportMessage;
    onRetry(clientId: string): void;
}

function OptimisticMessage({ message, isSendingMessage, onRetry }: OptimisticMessageProps) {
    return (
        <Message
            className={getOptimisticMessageClassName(message.status)}
            data-test={'supportWidget_optimisticMessage'}
            data-testid={'supportWidget_optimisticMessage'}
            model={{
                direction: 'outgoing',
                message: message.text,
                position: 'single',
                sentTime: format(new Date(message.createdAt), 'HH:mm'),
                type: 'text',
            }}
        >
            {message.attachments.length > 0 && (
                <Message.CustomContent>
                    {message.text && <div className={style.messageAttachmentText}>{message.text}</div>}
                    <div className={style.messageAttachments}>
                        {message.attachments.map(attachment => (
                            <span
                                key={attachment.localId}
                                className={style.optimisticAttachment}
                            >
                                <LocalSupportAttachmentPreview file={attachment.file} />
                            </span>
                        ))}
                    </div>
                </Message.CustomContent>
            )}
            {message.status !== 'sent' && (
                <Message.Footer>
                    <span
                        className={style.outgoingStatus}
                        data-test={'supportWidget_outgoingStatus'}
                        data-testid={'supportWidget_outgoingStatus'}
                        aria-live={'polite'}
                    >
                        {message.status === 'sending' ? (
                            <>
                                <span
                                    className={style.sendingSpinner}
                                    aria-hidden={'true'}
                                />
                                {translate('supportSending')}
                            </>
                        ) : (
                            <>
                                {translate('supportSendFailed')}
                                <button
                                    className={style.retrySendButton}
                                    type={'button'}
                                    onClick={() => onRetry(message.clientId)}
                                    disabled={isSendingMessage}
                                >
                                    {translate('supportRetrySend')}
                                </button>
                            </>
                        )}
                    </span>
                </Message.Footer>
            )}
        </Message>
    );
}

function getOptimisticMessageClassName(status: OutgoingSupportMessage['status']): string | undefined {
    if (status === 'sending') return style.sendingMessage;
    if (status === 'failed') return style.failedMessage;
    return undefined;
}

function isScrolledToEnd(element: HTMLElement): boolean {
    return Math.abs(element.scrollHeight - element.clientHeight - element.scrollTop) <= SCROLL_END_TOLERANCE_PX;
}
